/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { createContext, useContext, useEffect, useMemo } from 'react';

import { useRouter } from 'next/router';

import { parseCookies, setCookie } from 'nookies';

import { routesLabels } from '~/shared/constants/routesLabels';
import { cookies } from '~/shared/constants/cookies';

import { useToast } from '~/shared/hooks/useToast';
import { useAuth } from '~/modules/auth/hooks/useAuth';

import { withIsMounted } from '~/shared/utils/withIsMounted';
import { parsePermissionsByUser } from '~/shared/utils/parsePermissionsByUser';

import { WithChildren } from '~/shared/types/WithChildren';
import { IParsedPermissions } from '~/shared/interfaces/IParsedPermissions';

type IPermissionsContextProps = IParsedPermissions;
const PermissionsContext = createContext<IPermissionsContextProps>(
  {} as IPermissionsContextProps
);

const PermissionsProvider: WithChildren = withIsMounted(({ children }) => {
  const { currentAccess, currentAccessResale } = useAuth();
  const router = useRouter();
  const toast = useToast();

  const parsedCheckedPermissions = useMemo(() => {
    if (!currentAccess) {
      const { [cookies.PERMISSIONS]: defaultPermissions } = parseCookies();
      return defaultPermissions
        ? (JSON.parse(defaultPermissions) as IPermissionsContextProps)
        : null;
    }

    const parsed = parsePermissionsByUser(currentAccess, currentAccessResale);

    setCookie(null, cookies.PERMISSIONS, JSON.stringify(parsed), { path: '/' });
    return parsed;
  }, [currentAccess, currentAccessResale]);

  useEffect(() => {
    if (router.query?.forbidden) {
      const routesLabelsKeys = Object.keys(routesLabels);
      const forbiddenRoute = routesLabelsKeys.find((key) =>
        router.query.forbidden.includes(key)
      );

      const alias = routesLabels[forbiddenRoute];

      toast.show({
        variant: 'warning',
        title: `Você não tem permissão para acessar a tela de ${alias}!`,
        description:
          (router.query.description as string) ||
          'Entre em contato com um adiministrador.',
      });

      router.push({ pathname: router.pathname }, undefined, { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  return (
    <PermissionsContext.Provider value={{ ...parsedCheckedPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
});

const usePermissions = () => {
  return useContext(PermissionsContext);
};

export { usePermissions, PermissionsProvider };
